import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUmbrellaBeach, faPlus, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV, LEAVE_COLORS } from './nav';

const Leave: React.FC = () => {
    const { api } = useInternalApi();
    const [requests, setRequests] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [people, setPeople] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ internal_user_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '' });

    useEffect(() => { document.title = 'Leave & Cover - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const r = await api.get(`hr-api/leave/?${params}`);
            if (r.data?.status === 'success') {
                setRequests(r.data.data.requests || []);
                setSummary(r.data.data.summary);
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('hr-api/reference/').then(r => { if (r.data?.status === 'success') setPeople(r.data.data.people || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.internal_user_id || !form.start_date || !form.end_date) {
            Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Person, start and end dates are required.' });
            return;
        }
        try {
            await api.post('hr-api/leave/', form);
            setShowAdd(false);
            setForm({ internal_user_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not submit request.' });
        }
    };

    const decide = async (l: any, status: string) => {
        try { await api.patch(`hr-api/leave/${l.id}/`, { status }); load(); }
        catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update request.' }); }
    };

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUmbrellaBeach} className="me-2 text-primary" />Leave & Cover</h2>
                    <p className="text-muted mb-0">Who is off, who is going off, and what is awaiting a decision</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Request</button>
            </div>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Awaiting decision</div>
                            <h4 className={summary.pending ? 'text-warning' : ''}>{summary.pending}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">On leave today</div><h4>{summary.on_leave_today}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Upcoming (30 days)</div><h4>{summary.upcoming_30_days}</h4>
                        </div></div></div>
                    </div>

                    <select className="form-select mb-3" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">All statuses</option>
                        <option value="pending">Pending</option><option value="approved">Approved</option>
                        <option value="declined">Declined</option><option value="cancelled">Cancelled</option>
                    </select>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {requests.length === 0 ? (
                                <div className="text-center py-5 text-muted">No leave requests.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Person</th><th>Type</th><th>Dates</th><th>Days</th><th>Status</th><th>Decision</th></tr></thead>
                                    <tbody>
                                        {requests.map(l => (
                                            <tr key={l.id} className={l.is_current ? 'table-info' : ''}>
                                                <td>{l.person}{l.is_current && <span className="badge bg-info ms-2">Off now</span>}</td>
                                                <td className="small">{l.leave_type_label}</td>
                                                <td className="small">{l.start_date} → {l.end_date}</td>
                                                <td>{l.days}</td>
                                                <td><span className={`badge bg-${LEAVE_COLORS[l.status]}`}>{l.status}</span></td>
                                                <td>
                                                    {l.status === 'pending' ? (
                                                        <div className="d-flex gap-1">
                                                            <button className="btn btn-sm btn-outline-success" onClick={() => decide(l, 'approved')}><FontAwesomeIcon icon={faCheck} /></button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => decide(l, 'declined')}><FontAwesomeIcon icon={faTimes} /></button>
                                                        </div>
                                                    ) : <span className="small text-muted">{l.decided_by || '—'}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Leave Request</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Person</label>
                                    <select className="form-select" value={form.internal_user_id} onChange={(e) => setForm(f => ({ ...f, internal_user_id: e.target.value }))}>
                                        <option value="">Select…</option>
                                        {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Leave type</label>
                                    <select className="form-select" value={form.leave_type} onChange={(e) => setForm(f => ({ ...f, leave_type: e.target.value }))}>
                                        <option value="annual">Annual</option><option value="sick">Sick</option>
                                        <option value="parental">Parental</option><option value="compassionate">Compassionate</option>
                                        <option value="study">Study</option><option value="unpaid">Unpaid</option>
                                    </select>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">From</label>
                                        <input type="date" className="form-control" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">To</label>
                                        <input type="date" className="form-control" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Reason</label>
                                    <textarea className="form-control" rows={2} value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Submit</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Leave;
